import { authConstants } from '../../actions/auth/actionTypes';
import { UserStorageHelper } from '../../../helpers';
import { logger } from '../../../helpers/logger';
import produce from 'immer';
import { AnyAction } from 'redux';
import {
    FilesSharing,
    MappedLicenseStatusBody,
    NextcloudThemingCapabilities,
    NextcloudUser,
    SettingsItem,
    Theme,
} from '@sendent/nextcloud-api';

const user = UserStorageHelper.storeGetUser();

export type UserConfig = {
    // Nextcloud /cloud/user OCS payload; only id is read by the app
    userInfo: { id: string };
    // stored but never read, shapes in the comment below
    ping: unknown;
    status: unknown;
    licenseStatus: MappedLicenseStatusBody;
};

const initialState = {
    loggedIn: Boolean(user),
    loginFailed: false,
    loginErrorMessage: null as string | null,
    logout: false,
    user: (user ?? null) as NextcloudUser | null,
    userConfig: null as UserConfig | null,
    userSettings: null as SettingsItem[] | null,
    versionSettings: null as unknown,
    filesSharingSettings: null as FilesSharing | null,
    passwordPolicySettings: null as unknown,
    theme: null as Theme | null,
    nextcloudTheming: null as NextcloudThemingCapabilities | null,
    passwordDialogSkipped: false,
};

/*
user {
    "server":"https:\/\/cloud.example.com",
    "loginName":"username",
    "appPassword":
    "yKTVA4zgxjfivy52WqD8kW3M2pKGQr6srmUXMipRdunxjPFripJn0GMfmtNOqOolYSuJ6sCN"
}
userConfig.ping {
  "id": ,
  "userid": "",
  "dateconnected": ""
}
userConfig.status {
  "Version": "",
  "CurrentUserId": "",
  "App": "sendent",
  "DateLicenseEnd": "2024-06-20",
  "MaxUsers": "",
  "ValidLicense": true,
  "LicenseAction": "",
  "DateGracePeriodEnd": "2024-06-20",
  "MaxGraceUsers": "",
  "CurrentUserCount": ""
}
userConfig.licenseStatus {
  "status": "Current license is valid",
  "statusKind": "valid",
  "dateExpiration": "2024-06-20",
  "email": "",
  "level": "Premium",
  "licensekey": "",
  "dateLastCheck": "2021-09-10"
}
*/

export function authentication(state = initialState, action: AnyAction) {
    logger.debug('authentication reducer called', 'Auth');
    switch (action.type) {
        case authConstants.LOGIN_SUCCESS:
            logger.debug('authentication reducer: LOGIN_SUCCESS', 'Auth');
            return produce(state, (draft) => {
                draft.loggedIn = true;
                draft.loginFailed = false;
                draft.loginErrorMessage = null;
            });
        case authConstants.LOGOUT:
            logger.debug('authentication reducer: LOGOUT', 'Auth');
            return produce(state, (draft) => {
                draft.loggedIn = false;
                draft.logout = true;
                draft.user = null;
                draft.theme = null;
                draft.nextcloudTheming = null;
            });
        case authConstants.LOGIN_PROCESS:
            logger.debug('authentication reducer: LOGIN_PROCESS', 'Auth');
            return produce(state, (draft) => {
                draft.user = action.payload.user;
            });
        case authConstants.LOGIN_FAILED:
            logger.debug('authentication reducer: LOGIN_FAILED', 'Auth');
            return produce(state, (draft) => {
                draft.loginFailed = true;
                draft.loginErrorMessage = action.payload?.message ?? null;
            });
        case authConstants.APP_CHANGE_USER_CONFIG:
            logger.debug(
                'authentication reducer: APP_CHANGE_USER_CONFIG',
                'Auth',
            );
            return produce(state, (draft) => {
                draft.userConfig = action.payload.userConfig;
            });
        case authConstants.APP_CHANGE_USER_SETTINGS:
            logger.debug(
                'authentication reducer: APP_CHANGE_USER_SETTINGS',
                'Auth',
            );
            return produce(state, (draft) => {
                draft.userSettings = action.payload.userSettings;
                draft.versionSettings = action.payload.versionSettings;
                draft.filesSharingSettings =
                    action.payload.filesSharingSettings;
                draft.passwordPolicySettings =
                    action.payload.passwordPolicySettings;
                draft.theme = action.payload.theme;
                draft.nextcloudTheming = action.payload.nextcloudTheming;
            });
        case authConstants.APP_APPLY_USER_SETTINGS:
            logger.debug(
                'authentication reducer: APP_APPLY_USER_SETTINGS',
                'Auth',
            );
            return produce(state, (draft) => {
                draft.userSettings = action.payload.userSettings;
            });
        case authConstants.APP_CHANGE_NEXTCLOUD_THEMING:
            logger.debug(
                'authentication reducer: APP_CHANGE_NEXTCLOUD_THEMING',
                'Auth',
            );
            return produce(state, (draft) => {
                draft.nextcloudTheming = action.payload.nextcloudTheming;
            });
        case authConstants.PASSWORD_DIALOG_SKIPPED:
            logger.debug(
                'authentication reducer: PASSWORD_DIALOG_SKIPPED',
                'Auth',
            );
            return produce(state, (draft) => {
                draft.passwordDialogSkipped =
                    action.payload.passwordDialogSkipped;
            });
        default:
            return state;
    }
}
